import {
  Badge, Box, BreadcrumbGroup, Button, ButtonDropdown, Flashbar, Header, KeyValuePairs,
  SpaceBetween, Spinner, Table, Tabs,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from './apiFetch';
import { useDocumentTitle } from './useDocumentTitle';
import { getAwsCreds } from './awsCredentials';
import FmBundleDetail from './FmBundleDetail';
import EcsLogsModal from './EcsLogsModal';
import ConfirmActionModal from './ConfirmActionModal';
import { scaleEcsService } from './ecsActions';

const TYPE_LABELS: Record<string, string> = {
  git_repo: 'Git Repo',
  web_api_be: 'Web API BE',
  web_api_fe: 'Web API FE',
  fm_bundle: 'FM Bundle',
  custom: 'Custom',
};

const TYPE_ENDPOINTS: Record<string, string> = {
  web_api_be: 'web-api-backends',
  web_api_fe: 'web-api-frontends',
  git_repo: 'git-repo-projects',
  custom: 'custom-products',
};

/** All resource relation fields on a custom product that should be flattened into the resources table. */
const RESOURCE_RELATIONS = [
  { key: 'ec2_instances', typeLabel: 'EC2 Instance', assetType: 'ec2-instance' },
  { key: 'lambda_functions', typeLabel: 'Lambda Function', assetType: 'lambda-function' },
  { key: 'api_gateways', typeLabel: 'API Gateway', assetType: 'api-gateway' },
  { key: 'ecs_services', typeLabel: 'ECS Service', assetType: 'ecs-service' },
  { key: 'rds_instances', typeLabel: 'RDS Instance', assetType: 'rds-instance' },
  { key: 'dynamodb_tables', typeLabel: 'DynamoDB Table', assetType: 'dynamodb-table' },
  { key: 'security_groups', typeLabel: 'Security Group', assetType: 'security-group' },
  { key: 'load_balancers', typeLabel: 'Load Balancer', assetType: 'load-balancer' },
  { key: 'cloudfront_distributions', typeLabel: 'CloudFront Distribution', assetType: 'cloudfront-distribution' },
  { key: 'route53_hosted_zones', typeLabel: 'Route 53 Hosted Zone', assetType: 'route53-hosted-zone' },
  { key: 'acm_certificates', typeLabel: 'ACM Certificate', assetType: 'acm-certificate' },
  { key: 's3_buckets', typeLabel: 'S3 Bucket', assetType: 's3-bucket' },
  { key: 'cognito_user_pools', typeLabel: 'Cognito User Pool', assetType: 'cognito-user-pool' },
];

/**
 * Asset types that have a working detail page via InventoryDetailPage
 * (route: /inventory/:assetType/:id). Extend this set as InventoryDetailPage
 * gains support for additional resource types.
 */
const SUPPORTED_DETAIL_ASSET_TYPES = new Set<string>([
  'ec2-instance',
  'lambda-function',
  'api-gateway',
  'ecs-service',
  'rds-instance',
  'dynamodb-table',
]);

// Strapi 5 rejects comma-separated populate strings with a 400 ValidationError.
// Build indexed array notation instead: populate[0]=x&populate[1]=y&...
const CUSTOM_POPULATE = RESOURCE_RELATIONS.map(r => r.key)
  .concat('account')
  .map((key, index) => `populate[${index}]=${encodeURIComponent(key)}`)
  .join('&');

// Standard products (web_api_be/web_api_fe/git_repo) relate to AWS only through their stack:
// account + Region live on the stack, and ECS services hang off the stack as `ecs_services`.
// Strapi 5 rejects comma-separated populate; use nested object notation to load stack.account,
// stack.Region (a scalar, included automatically when the stack itself is populated), and
// stack.ecs_services for the "View ECS Logs" action.
const STANDARD_POPULATE =
  'populate[stack][populate][account]=true&populate[stack][populate][ecs_services]=true';

interface FlatResource {
  name: string;
  type: string;
  arn: string;
  region: string;
  documentId: string | null;
  assetType: string;
}

export default function ProductDetailPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<any>(null);
  useDocumentTitle(product ? (product.Name || product.name) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: 'success' | 'error' | 'info'; content: string } | null>(null);
  const [resyncLoading, setResyncLoading] = useState(false);
  const [ecsLogsVisible, setEcsLogsVisible] = useState(false);

  // Product-level Stop/Start flow (currently covers ECS services; framed to
  // extend to other resource types such as RDS in future).
  const [scaleAction, setScaleAction] = useState<"stop" | "start" | null>(null);
  const [scaling, setScaling] = useState(false);

  useEffect(() => {
    if (!id || !type) { setLoading(false); return; }
    // FM bundles are rendered by the dedicated <FmBundleDetail> branch below, which loads the
    // bundle itself from /fm-bundles/:id — skip the generic stack-based fetch (there's no
    // TYPE_ENDPOINTS entry and no stack to populate).
    if (type === 'fm_bundle') { setLoading(false); return; }
    const endpoint = TYPE_ENDPOINTS[type];
    if (!endpoint) { setError(`Unknown product type: ${type}`); setLoading(false); return; }

    const populateParam = type === 'custom'
      ? `?${CUSTOM_POPULATE}`
      : `?${STANDARD_POPULATE}`;

    apiFetch(`/${endpoint}/${id}${populateParam}`)
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => setProduct(json.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [type, id]);

  const handleResync = async () => {
    if (!product || !id) return;
    const accountAlias = product.account?.Alias;
    if (!accountAlias) {
      setFlash({ type: 'error', content: 'No account linked to this product. Cannot determine AWS credentials.' });
      return;
    }
    const creds = getAwsCreds(accountAlias);
    if (!creds) {
      setFlash({ type: 'error', content: `AWS credentials not configured for '${accountAlias}'. Go to Settings to set them up.` });
      return;
    }
    setResyncLoading(true);
    setFlash(null);
    try {
      const res = await apiFetch(`/custom-products/${id}/resync`, {
        method: 'POST',
        headers: {
          'X-AWS-Access-Key-Id': creds.accessKeyId,
          'X-AWS-Secret-Access-Key': creds.secretAccessKey,
          'X-AWS-Session-Token': creds.sessionToken,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error?.message || `Resync failed: ${res.status}`);
      }
      const data = await res.json();
      const { results } = data;
      setFlash({
        type: 'success',
        content: `Resync complete: ${results?.totalDiscovered ?? 0} discovered, ${results?.synced ?? 0} synced, ${results?.new ?? 0} new, ${results?.updated ?? 0} updated, ${results?.unsupported ?? 0} unsupported, ${results?.errors ?? 0} errors.`,
      });
      // Re-fetch the product to get updated relations
      const refreshRes = await apiFetch(`/custom-products/${id}?${CUSTOM_POPULATE}`);
      if (refreshRes.ok) {
        const refreshed = await refreshRes.json();
        setProduct(refreshed.data);
      }
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally {
      setResyncLoading(false);
    }
  };

  // FM bundle detail: rendered early so it never runs the generic stack fetch / populate=stack
  // path. FmBundleDetail loads the bundle itself from /fm-bundles/:id.
  if (type === 'fm_bundle') {
    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/' },
            { text: 'Projects', href: '/projects' },
            { text: 'FM Bundle', href: '#' },
          ]}
          onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <FmBundleDetail id={id!} />
      </SpaceBetween>
    );
  }

  if (loading) return <Spinner size="large" />;
  if (error) return <Box color="text-status-error" fontSize="heading-m">Error: {error}</Box>;
  if (!product) return <Box color="text-body-secondary">Product not found</Box>;

  // Custom product detail view
  if (type === 'custom') {
    const resources: FlatResource[] = [];
    for (const rel of RESOURCE_RELATIONS) {
      const items = product[rel.key];
      if (Array.isArray(items)) {
        for (const item of items) {
          resources.push({
            name: item.name || item.Name || item.functionName || item.tableName || item.bucketName || item.domainName || '—',
            type: rel.typeLabel,
            arn: item.arn || item.ARN || '—',
            region: item.region || item.Region || product.region || '—',
            documentId: item.documentId || null,
            assetType: rel.assetType,
          });
        }
      }
    }

    const unsupportedResources: { arn: string; resourceType: string }[] =
      Array.isArray(product.unsupportedResources) ? product.unsupportedResources : [];

    const accountDisplay = product.account
      ? `${product.account.Alias} (${product.account.accountid})`
      : '—';

    const kvItems = [
      { label: 'Name', value: product.name || '—' },
      { label: 'Description', value: product.description || '—' },
      { label: 'Tag', value: product.tagKey && product.tagValue ? `${product.tagKey}=${product.tagValue}` : '—' },
      { label: 'Region', value: product.region || '—' },
      { label: 'Account', value: accountDisplay },
      { label: 'Last Synced', value: product.lastSyncedAt ? new Date(product.lastSyncedAt).toLocaleString() : '—' },
    ];

    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/' },
            { text: 'Projects', href: '/projects' },
            { text: product.name, href: '#' },
          ]}
          onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
        />
        {flash && (
          <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
        )}
        <Header
          variant="h1"
          actions={
            <Button onClick={handleResync} loading={resyncLoading} iconName="refresh">
              Resync
            </Button>
          }
        >
          {product.name} <Badge>{TYPE_LABELS.custom}</Badge>
        </Header>
        <KeyValuePairs items={kvItems} />
        <Tabs
          tabs={[
            {
              id: 'resources',
              label: `Resources (${resources.length})`,
              content: (
                <Table
                  variant="embedded"
                  items={resources}
                  columnDefinitions={[
                    {
                      id: 'name',
                      header: 'Name',
                      cell: i => {
                        if (SUPPORTED_DETAIL_ASSET_TYPES.has(i.assetType) && i.documentId) {
                          const href = `/inventory/${i.assetType}/${i.documentId}`;
                          return (
                            <a href={href} onClick={e => { e.preventDefault(); navigate(href); }}>{i.name}</a>
                          );
                        }
                        return i.name;
                      },
                    },
                    { id: 'type', header: 'Type', cell: i => i.type },
                    { id: 'arn', header: 'ARN', cell: i => i.arn },
                    { id: 'region', header: 'Region', cell: i => i.region },
                  ]}
                  empty={<Box textAlign="center" color="text-body-secondary">No resources linked</Box>}
                />
              ),
            },
            {
              id: 'unsupported',
              label: `Unsupported Resources (${unsupportedResources.length})`,
              content: (
                <Table
                  variant="embedded"
                  items={unsupportedResources}
                  columnDefinitions={[
                    { id: 'resourceType', header: 'Resource Type', cell: i => i.resourceType },
                    { id: 'arn', header: 'ARN', cell: i => i.arn },
                  ]}
                  empty={<Box textAlign="center" color="text-body-secondary">No unsupported resources</Box>}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>
    );
  }

  // Standard product detail view (git_repo, web_api_be, web_api_fe)
  const stackLink = product.stack
    ? <a href={`/stacks/${product.stack.documentId}`} onClick={e => { e.preventDefault(); navigate(`/stacks/${product.stack.documentId}`); }}>{product.stack.name}</a>
    : '—';

  const kvItems = type === 'git_repo'
    ? [
        { label: 'Name', value: product.Name || '—' },
        { label: 'Repo Name', value: product.repo_name || '—' },
        { label: 'Stack', value: stackLink },
      ]
    : [
        { label: 'Name', value: product.Name || '—' },
        { label: 'Domain', value: product.domain || '—' },
        { label: 'Stack', value: stackLink },
      ];

  // ECS services hang off the stack; account alias + region live on the stack too.
  const ecsServices: any[] = Array.isArray(product.stack?.ecs_services) ? product.stack.ecs_services : [];
  const hasEcsServices = ecsServices.length > 0;
  const stackAccountAlias: string | null = product.stack?.account?.Alias ?? null;
  const stackRegion: string | null = product.stack?.Region ?? null;

  // desiredCount is populated on stack.ecs_services (scalar field), so the
  // Stop/Start actions can be gated on the current cached state:
  //  - Stop is useful only if at least one service is running (desiredCount > 0)
  //  - Start is useful only if at least one service is stopped (desiredCount === 0)
  const anyEcsRunning = ecsServices.some((s) => (s.desiredCount ?? 0) > 0);
  const anyEcsStopped = ecsServices.some((s) => (s.desiredCount ?? 0) === 0);

  const clusterNameOf = (s: any): string =>
    (s.clusterArn ? s.clusterArn.split('/').pop() : undefined) ?? s.cluster ?? '';

  const runProductScale = async () => {
    if (!scaleAction) return;
    if (!stackAccountAlias) {
      setScaleAction(null);
      setFlash({ type: 'error', content: 'No account linked to this product\u2019s stack. Cannot determine AWS credentials.' });
      return;
    }
    const creds = getAwsCreds(stackAccountAlias);
    if (!creds) {
      setScaleAction(null);
      setFlash({ type: 'error', content: `AWS credentials not configured for '${stackAccountAlias}'. Go to Settings to set them up.` });
      return;
    }
    if (!stackRegion) {
      setScaleAction(null);
      setFlash({ type: 'error', content: 'No region found on this product\u2019s stack.' });
      return;
    }

    const desiredCount: 0 | 1 = scaleAction === 'start' ? 1 : 0;
    setScaling(true);

    const succeeded: string[] = [];
    const failed: { serviceName: string; message: string }[] = [];

    for (const s of ecsServices) {
      const serviceName = s.serviceName || s.arn || s.documentId;
      const clusterName = clusterNameOf(s);
      try {
        await scaleEcsService({ clusterName, serviceName, region: stackRegion, desiredCount, creds });
        succeeded.push(serviceName);
      } catch (e: any) {
        failed.push({ serviceName, message: e.message });
      }
    }

    setScaling(false);
    setScaleAction(null);

    const verb = scaleAction === 'start' ? 'Started' : 'Stopped';
    if (failed.length === 0) {
      setFlash({ type: 'success', content: `${verb} ${succeeded.length} service(s).` });
    } else if (succeeded.length === 0) {
      setFlash({ type: 'error', content: `Failed to ${scaleAction} service(s): ${failed.map((f) => `${f.serviceName}: ${f.message}`).join('; ')}` });
    } else {
      setFlash({
        type: 'error',
        content: `${verb} ${succeeded.length} service(s); ${failed.length} failed: ${failed.map((f) => `${f.serviceName}: ${f.message}`).join('; ')}`,
      });
    }

    // Re-fetch the product so the cached desiredCount reflects the change.
    const populateParam = `?${STANDARD_POPULATE}`;
    const endpoint = TYPE_ENDPOINTS[type!];
    if (endpoint) {
      const refreshRes = await apiFetch(`/${endpoint}/${id}${populateParam}`);
      if (refreshRes.ok) {
        const refreshed = await refreshRes.json();
        setProduct(refreshed.data);
      }
    }
  };

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Home', href: '/' },
          { text: 'Projects', href: '/projects' },
          { text: product.Name || product.name, href: '#' },
        ]}
        onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
      />
      <Header
        variant="h1"
        actions={
          <ButtonDropdown
            items={[
              {
                id: 'view-ecs-logs',
                text: 'View ECS Logs',
                disabled: !hasEcsServices,
                disabledReason: hasEcsServices ? undefined : 'No ECS service is linked to this product\u2019s stack.',
              },
              {
                id: 'stop',
                text: 'Stop',
                disabled: !hasEcsServices || !anyEcsRunning,
                disabledReason: !hasEcsServices
                  ? 'No ECS service is linked to this product\u2019s stack.'
                  : (!anyEcsRunning ? 'All ECS services are already stopped (desired count 0).' : undefined),
              },
              {
                id: 'start',
                text: 'Start',
                disabled: !hasEcsServices || !anyEcsStopped,
                disabledReason: !hasEcsServices
                  ? 'No ECS service is linked to this product\u2019s stack.'
                  : (!anyEcsStopped ? 'All ECS services are already running (desired count > 0).' : undefined),
              },
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'view-ecs-logs') setEcsLogsVisible(true);
              if (detail.id === 'stop') setScaleAction('stop');
              if (detail.id === 'start') setScaleAction('start');
            }}
          >
            Actions
          </ButtonDropdown>
        }
      >
        {product.Name || product.name} <Badge>{TYPE_LABELS[type!] ?? type}</Badge>
      </Header>
      <KeyValuePairs items={kvItems} />
      <EcsLogsModal
        visible={ecsLogsVisible}
        onDismiss={() => setEcsLogsVisible(false)}
        ecsServices={ecsServices.map((s) => ({
          documentId: s.documentId,
          serviceName: s.serviceName || s.arn || s.documentId,
          containers: Array.isArray(s.containers) ? s.containers : [],
        }))}
        accountAlias={stackAccountAlias}
        region={stackRegion}
      />
      <ConfirmActionModal
        visible={scaleAction !== null}
        onDismiss={() => setScaleAction(null)}
        onConfirm={runProductScale}
        loading={scaling}
        header={scaleAction === 'start' ? 'Start ECS services' : 'Stop ECS services'}
        confirmLabel={scaleAction === 'start' ? 'Start' : 'Stop'}
      >
        <SpaceBetween size="s">
          <Box>
            {scaleAction === 'start'
              ? 'This will set the desired task count to 1 for the following ECS service(s):'
              : 'This will set the desired task count to 0 for the following ECS service(s):'}
          </Box>
          <Table
            variant="embedded"
            items={ecsServices}
            columnDefinitions={[
              { id: 'serviceName', header: 'Service', cell: (s) => s.serviceName || s.arn || s.documentId },
              { id: 'cluster', header: 'Cluster', cell: (s) => clusterNameOf(s) || '\u2014' },
              { id: 'current', header: 'Current desired', cell: (s) => String(s.desiredCount ?? 0) },
              { id: 'target', header: 'Target desired', cell: () => (scaleAction === 'start' ? '1' : '0') },
            ]}
          />
        </SpaceBetween>
      </ConfirmActionModal>
    </SpaceBetween>
  );
}

